import type { UnwrapNestedRefs } from 'vue';
import type { MoviePlayerState } from 'src/models/moviePlayer';

// 動画プレイヤーの表示アスペクト比（16:9固定）。実行中に変化しない値のためモジュール定数として定義する。
const VIDEO_ASPECT_RATIO = 16 / 9;

export interface PlaybackVjsConfig {
  // video.jsのprepare完了ポーリング対象vid一覧
  prepareVideoIds: () => string[];
  // 全video要素にprepareVideojs()を呼ぶ
  prepareVideoRefs: () => void;
}

export interface PlaybackVjsContext {
  videoAspectRatio: number;
  onVjsError: (vid: string) => void;
  videoPlayerHasError: () => boolean;
  onVjsPrepared: (vid: string) => void;
  prepareVideoPlayers: () => Promise<void>;
  onVjsLoadedData: (vid: string) => void;
  selectUrlForVid: (vid: string) => string;
  // vjsPrepared状態の初期化（対象vid一覧をfalseで初期化する）。
  // オーケストレーション層からのvjsPrepared直接初期化を避けるために公開する。
  initVjsPreparedState: (vids: string[]) => void;
  // vjsエラー状態のクリア。resetVideoPlayers（動画遷移側）からの委譲用。
  clearVjsErrors: () => void;
}

// vjsライフサイクル管理（エラー監視/prepare完了監視/ロード完了監視/再生URL選択）を担うcomposable。
export function useMoviePlayerPlaybackVjs(
  state: UnwrapNestedRefs<MoviePlayerState>,
  config: PlaybackVjsConfig,
): PlaybackVjsContext {
  const videoAspectRatio = VIDEO_ASPECT_RATIO;

  const onVjsError = (vid: string) => {
    state.playback.vjsError.push(vid);
  };
  const videoPlayerHasError = () => {
    return state.playback.vjsError.length > 0;
  };
  const clearVjsErrors = () => {
    state.playback.vjsError = [];
  };
  const onVjsPrepared = (vid: string) => {
    state.playback.vjsPrepared[vid] = true;
  };
  const initVjsPreparedState = (vids: string[]) => {
    state.playback.vjsPrepared = vids.reduce<Record<string, boolean>>((acc, vid) => {
      acc[vid] = false;
      return acc;
    }, {});
  };
  const prepareVideoPlayers = async() => {
    config.prepareVideoRefs();
    const vids = config.prepareVideoIds();
    await Promise.all(vids.map(vid => {
      return new Promise<void>((resolve) => {
        const loopFunc = () => {
          if (state.playback.vjsPrepared[vid]) return resolve();

          window.setTimeout(loopFunc, 100);
        };
        loopFunc();
      });
    }));
  };
  const onVjsLoadedData = (vid: string) => {
    state.playback.vjsLoadedData[vid] = true;
  };
  // TODO: MoviePlayerCompareMode改修時に同関数を統合した上、moviePlayerMixin内に移動する。
  const selectUrlForVid = (vid: string) => {
    if (state.movieList.current.movieFileUrlObj) {
      if (vid.startsWith('vid1-')) {
        return state.movieList.current.movieFileUrlObj.left;
      } else if (vid.startsWith('vid2-')) {
        return state.movieList.current.movieFileUrlObj.front;
      } else if (vid.startsWith('vid3-')) {
        return state.movieList.current.movieFileUrlObj.right;
      }
    }
    return '';
  };

  return {
    videoAspectRatio,
    onVjsError,
    videoPlayerHasError,
    onVjsPrepared,
    prepareVideoPlayers,
    onVjsLoadedData,
    selectUrlForVid,
    initVjsPreparedState,
    clearVjsErrors,
  };
}
